import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { X } from '@phosphor-icons/react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEADER_IMAGE = "https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/0ysoa4g0_Capture%20d%E2%80%99e%CC%81cran%202026-03-23%20a%CC%80%2000.29.28.png";

const GalleryPage = () => {
  const { t, getLocalizedField } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setImages(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const getCaption = (image) => {
    return getLocalizedField(image, 'caption') || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="skeleton h-12 w-64 mb-8 rounded" />
          <div className="gallery-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton aspect-square rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="gallery-page">
      {/* Hero Header */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img src={HEADER_IMAGE} alt="Calvi Fishing - Vue sur la citadelle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/70 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-4">{t('gallery.title')}</h1>
          <p className="text-white/80 text-lg max-w-2xl">{t('gallery.subtitle')}</p>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {images.length === 0 ? (
          <p className="text-center text-[#4B5563] py-20">Aucune image pour le moment</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => {
              const caption = getCaption(image);
              return (
                <div 
                  key={image.id} 
                  className={`relative aspect-square overflow-hidden rounded-xl cursor-pointer group animate-fade-up stagger-${Math.min(index + 1, 5)}`}
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-item-${index}`}
                >
                  <img 
                    src={image.image_url} 
                    alt={caption || 'Calvi Fishing'} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Caption overlay on hover */}
                  {caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-sm font-medium">{caption}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          {selectedImage && (
            <div className="relative">
              <img src={selectedImage.image_url} alt={getCaption(selectedImage) || 'Calvi Fishing'} className="w-full rounded-lg" />
              {getCaption(selectedImage) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                  <p className="text-white text-lg font-medium">{getCaption(selectedImage)}</p>
                </div>
              )}
              <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors">
                <X size={24} />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
